import type { ReactNode } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import Markdown, { type ASTNode, type RenderRules } from 'react-native-markdown-display';
import { colors } from '../../../theme/colors';
import { darkPrecisionFontFamilies } from '../../../theme/typography';

export const TABLE_CELL_MIN_WIDTH = 140;

const tableRules: RenderRules = {
  table: (node: ASTNode, children: ReactNode[], _parent: ASTNode[], styles) => (
    <ScrollView
      key={node.key}
      horizontal
      showsHorizontalScrollIndicator
      contentContainerStyle={tableScrollStyles.content}
    >
      <View style={[styles._VIEW_SAFE_table, tableScrollStyles.table]}>{children}</View>
    </ScrollView>
  ),
};

interface ChatMarkdownProps {
  readonly content: string;
}

// Web-only renderer. The native sibling (ChatMarkdown.tsx) imports
// react-native-enriched-markdown, which is a Fabric/codegen component
// that crashes the web bundle on module evaluation. Metro's platform
// resolution picks this `.web.tsx` over the default file on web builds,
// keeping the native dependency out of the web JavaScript entirely.
export function ChatMarkdown({ content }: ChatMarkdownProps) {
  return (
    <Markdown style={markdownStyles} rules={tableRules}>
      {content}
    </Markdown>
  );
}

const tableScrollStyles = StyleSheet.create({
  content: {
    flexGrow: 1,
  },
  table: {
    flexGrow: 1,
  },
});

const markdownStyles = StyleSheet.create({
  body: {
    color: colors.text.secondary,
    fontSize: 14,
    lineHeight: 20,
  },
  paragraph: {
    marginTop: 0,
    marginBottom: 8,
  },
  heading1: {
    color: colors.text.primary,
    fontSize: 20,
    fontWeight: '700',
    marginTop: 4,
    marginBottom: 8,
  },
  heading2: {
    color: colors.text.primary,
    fontSize: 17,
    fontWeight: '700',
    marginTop: 4,
    marginBottom: 6,
  },
  heading3: {
    color: colors.text.primary,
    fontSize: 15,
    fontWeight: '600',
    marginTop: 4,
    marginBottom: 6,
  },
  strong: {
    color: colors.text.primary,
    fontWeight: '700',
  },
  em: {
    color: colors.text.primary,
    fontStyle: 'italic',
  },
  link: {
    color: colors.primary.neon,
    textDecorationLine: 'underline',
  },
  bullet_list: {
    marginBottom: 6,
  },
  ordered_list: {
    marginBottom: 6,
  },
  list_item: {
    marginBottom: 2,
  },
  bullet_list_icon: {
    color: colors.primary.neon,
    marginRight: 8,
  },
  ordered_list_icon: {
    color: colors.text.tertiary,
    marginRight: 8,
  },
  code_inline: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 12,
    color: colors.text.primary,
    backgroundColor: colors.surface.row,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  code_block: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 12,
    color: colors.text.primary,
    backgroundColor: colors.surface.row,
    borderColor: colors.border.subtle,
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },
  fence: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 12,
    color: colors.text.primary,
    backgroundColor: colors.surface.row,
    borderColor: colors.border.subtle,
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },
  blockquote: {
    color: colors.text.tertiary,
    backgroundColor: colors.surface.row,
    borderLeftColor: colors.primary.muted,
    borderLeftWidth: 3,
    paddingLeft: 10,
    marginBottom: 8,
  },
  table: {
    borderColor: colors.border.subtle,
    borderWidth: 1,
    borderRadius: 6,
    marginBottom: 8,
  },
  thead: {
    backgroundColor: colors.surface.row,
  },
  th: {
    minWidth: TABLE_CELL_MIN_WIDTH,
    padding: 8,
    color: colors.text.primary,
    fontWeight: '600',
  },
  tr: {
    borderBottomColor: colors.border.subtle,
    borderBottomWidth: 1,
  },
  td: {
    minWidth: TABLE_CELL_MIN_WIDTH,
    padding: 8,
    color: colors.text.secondary,
  },
});
